/**
 * NotificationsInbox — list of user_notifications. Per Feature #4 spec
 * §HR-on-behalf-of: when HR generates a Relational analysis involving you,
 * you see the existence (this page), not the content.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { SectionHeader, Callout } from "../components/index.js";

const KIND_LABEL = {
  hr_on_behalf_relational: "On-behalf-of",
  auto_pin_created:        "Snapshot pinned",
  consent_redeemed:        "Consent redeemed",
  morning_digest_ready:    "Morning digest",
  fairness_audit_alert:    "Consistency audit",
};

export default function NotificationsInbox() {
  const { session } = useSession();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const params = filter === "unread" ? "?unread_only=true" : "";
      const res = await fetch(`/.netlify/functions/notifications${params}`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || "Failed to load");
      else setItems(body.notifications || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [session, filter]);

  const act = async (notification_id, action) => {
    await fetch("/.netlify/functions/notifications", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ notification_id, action }),
    });
    refresh();
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to see notifications.</p>;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader index="∗" title="Notifications" />

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>All</FilterButton>
        <FilterButton active={filter === "unread"} onClick={() => setFilter("unread")}>Unread</FilterButton>
      </div>

      {loading && <p style={{ color: "var(--ppr-color-text-muted)" }}>Loading…</p>}
      {error && <ErrorBox error={error} />}

      {!loading && items.length === 0 && (
        <Callout.Tip>
          You're caught up. New notifications appear here when a Pro account generates a relational analysis involving you, when an invite recipient consents, or when the morning digest is ready.
        </Callout.Tip>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((n) => <NotificationRow key={n.notification_id} item={n} onAct={act} />)}
      </div>
    </div>
  );
}

function NotificationRow({ item, onAct }) {
  const isUnread = !item.read_at;
  const kindLabel = KIND_LABEL[item.kind] || item.kind;
  return (
    <article
      style={{
        padding: "14px 16px",
        border: "1px solid var(--ppr-color-border-default)",
        background: isUnread
          ? "var(--ppr-color-accent-pale-soft)"
          : "var(--ppr-color-background-surface)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "11px",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-navy)",
        }}>
          {kindLabel}
        </span>
        <span style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "11px",
          color: "var(--ppr-color-text-muted)",
        }}>
          {new Date(item.emitted_at).toLocaleString()}
        </span>
      </div>
      <div style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "16px",
        color: "var(--ppr-color-text-primary)",
        marginBottom: 4,
      }}>
        {item.title}
      </div>
      {item.body && (
        <div style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          color: "var(--ppr-color-text-secondary)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
        }}>
          {item.body}
        </div>
      )}
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        {item.link_path && <Link to={item.link_path} style={linkBtn}>Open</Link>}
        {isUnread && <button onClick={() => onAct(item.notification_id, "read")} style={ghostBtn}>Mark read</button>}
        <button onClick={() => onAct(item.notification_id, "dismiss")} style={ghostBtn}>Dismiss</button>
      </div>
    </article>
  );
}

function FilterButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        padding: "6px 12px",
        border: "1px solid var(--ppr-color-border-default)",
        background: active ? "var(--ppr-color-accent-navy)" : "var(--ppr-color-background-surface)",
        color: active ? "var(--ppr-color-text-inverse)" : "var(--ppr-color-accent-navy)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const linkBtn = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "11px",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  padding: "6px 10px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  textDecoration: "none",
};

const ghostBtn = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "11px",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  padding: "6px 10px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-secondary)",
  cursor: "pointer",
};

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
      marginBottom: 16,
    }}>
      {error}
    </div>
  );
}
